import { faFlag, faFlask, faMicrochip } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BasicTable,
  BasicTableCell,
  BasicTableRow,
  Button,
  TableColumnHeader,
  TableColumnInstance,
} from '@orobix/orobix-fe-components'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createUseStyles } from 'react-jss'

import { Observation } from '../../../types/observation'
import { Sorting, SortingDirection } from '../../../types/sorting'
import { titleUndeletableObservation } from '../../ObservationsSelectField/utils'
import { SortButton } from '../../SortButton/SortButton'
import useIconRowStyles from './iconsRow.styles'
import { ItemProps, ViewProps } from './types'

const useSortStyles = createUseStyles({
  sortableColumn: { display: 'flex' },
})

export const TabularView = ({
  children,
  observations,
  onItemClick,
  tagDecorator,
  menuContextualItems,
  emptyMessage,
  tableSorting,
  onColumnSort,
}: ViewProps) => {
  const columns = useColumns(tableSorting, onColumnSort)
  const { t } = useTranslation()

  return (
    <BasicTable<Observation, keyof Observation>
      columns={columns}
      isEmpty={observations?.length === 0}
      alternateRows={false}
      compact
      emptyMessage={
        emptyMessage || t("There's nothing here. Try uploading a new image.")
      }
    >
      {observations?.map((o) =>
        children ? (
          // A good improvement of this part of code could be to forward view props
          // to children, so that the render item function can use props provided to
          // observations browser.
          children(o)
        ) : (
          <TabularItem
            key={o.id}
            observation={o}
            onClick={onItemClick}
            tagDecorator={tagDecorator}
            menuContextualItems={menuContextualItems}
          />
        )
      )}
    </BasicTable>
  )
}

export const TabularItem = ({
  observation,
  onClick,
  tagDecorator,
  menuContextualItems,
  isActive: isActiveInitial,
  isDisabled: isDisabledInitial,
}: ItemProps) => {
  const [isActive, setActive] = useState(isActiveInitial)
  const [isDisabled, setDisabled] = useState(isDisabledInitial)
  const { t } = useTranslation()
  const iconClasses = useIconRowStyles()

  /** Executes given `callback` when `isDisabled` is `false` */
  const withMiddleware = (callback: () => void) => () => {
    if (!isDisabled) {
      callback()
    }
  }

  const renderMenuItems = () => {
    if (!menuContextualItems) return null

    const menu = menuContextualItems({
      observation,
      setDisabled,
      setActive,
    })

    return menu?.map((item, i) =>
      ((observation.trainingIds.length > 0 ||
        observation.trainingTestIds.length > 0) &&
        item.type !== 'destroy') ||
      (observation.trainingIds.length <= 0 &&
        observation.trainingTestIds.length <= 0) ? (
        <Button
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          buttonType="button"
          type={item.type === 'destroy' ? 'blank-destroy' : 'blank-default'}
          onClick={(e) => {
            e.stopPropagation()
            withMiddleware(item.onClick)()
          }}
          label={item.label}
          icon={item.icon}
          hideLabel={item.hideLabel}
          title={item.label}
        />
      ) : (
        <Button
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          buttonType="button"
          type="blank-default"
          onClick={undefined}
          label={item.label}
          icon={item.icon}
          hideLabel
          disabled
          title={t('Image used for trainings and/or tests')}
        />
      )
    )
  }

  return (
    <BasicTableRow
      rowData={observation}
      selected={isActive}
      compact
      muted={isDisabled}
      onClick={withMiddleware(
        () => onClick && onClick({ observation, setDisabled, setActive })
      )}
    >
      <BasicTableCell th>#{observation.id}</BasicTableCell>
      <BasicTableCell>{observation.filename}</BasicTableCell>
      <BasicTableCell>
        <div className={iconClasses.iconsRow}>
          {tagDecorator && tagDecorator({ observation, iconOnly: true })}
          {observation.annotationIsPromoted && (
            <FontAwesomeIcon
              icon={faMicrochip}
              title={t('Annotation promoted to ground truth')}
            />
          )}
          {observation.trainingIds.length > 0 && (
            <FontAwesomeIcon
              className={iconClasses.flagIcon}
              icon={faFlag}
              title={titleUndeletableObservation(observation.trainingIds, [])}
            />
          )}
          {observation.trainingTestIds.length > 0 && (
            <FontAwesomeIcon
              icon={faFlask}
              title={titleUndeletableObservation(
                [],
                observation.trainingTestIds
              )}
            />
          )}
        </div>
      </BasicTableCell>
      <BasicTableCell>
        {tagDecorator
          ? tagDecorator({ observation, hideIcon: true })
          : observation.productClasses?.join()}
      </BasicTableCell>
      <BasicTableCell align="right" noWrap>
        {renderMenuItems()}
      </BasicTableCell>
    </BasicTableRow>
  )
}

const useColumns = (
  tableSorting?: Sorting,
  onColumnSort?: (field: string, direction: SortingDirection) => void
): TableColumnInstance<Observation, keyof Observation>[] => {
  const { t } = useTranslation()
  const sortClasses = useSortStyles()

  const filenameSort = tableSorting?.filename
  const dateSort = tableSorting?.creationDate

  return [
    {
      id: 'id',
      header: t('Id'),
      th: true,
    },
    {
      id: 'filename',
      header: (
        <div className={sortClasses.sortableColumn}>
          <TableColumnHeader
            title={t('Image Name')}
            sorting={
              <SortButton
                sort={filenameSort}
                onSort={(dir) => onColumnSort?.('filename', dir)}
              />
            }
          />
        </div>
      ),
    },
    {
      id: 'creationDate',
      header: (
        <div className={sortClasses.sortableColumn}>
          <TableColumnHeader
            title={t('Uploaded on')}
            sorting={
              <SortButton
                sort={dateSort}
                onSort={(dir) => onColumnSort?.('creationDate', dir)}
              />
            }
          />
        </div>
      ),
    },
    {
      id: 'trainingIds',
      header: '',
      width: 0,
    },
    {
      id: 'productClasses',
      header: t('Classes'),
    },
    {
      id: 'image',
      header: t('Actions'),
      noWrap: true,
      align: 'right',
      width: 0,
    },
  ]
}
